import type { TournamentDetail } from '@drawgon/shared';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchTournament } from '../lib/api';
import {
  championOf,
  roundName,
  roundsOf,
  type BracketMatch,
  type BracketSide,
} from '../lib/bracket';
import {
  alert,
  alertTitle,
  badgeYellow,
  bracketSlot,
  heading,
  lead,
  panel,
  sectionTitle,
} from '../lib/ui';

type State =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; detail: TournamentDetail };

/**
 * トーナメント表（企画書 §17.2 / §19、#151）。
 *
 * **ログイン不要で見られることが重要。** 通知からアプリに戻れない人（アプリを消した・別の端末）
 * の受け皿になる（`docs/specs/screen-flow.md`）。
 *
 * **iOS はこの木を描かない**（#149）。小さな画面ではどれが自分か分からなくなるので、
 * あちらは自分の選手のみちのりだけを出す。**全体の形を見せるのはここの仕事。**
 */
export function TournamentBracket() {
  const { id = '' } = useParams();
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    fetchTournament(id)
      .then((detail) => {
        if (!cancelled) setState({ status: 'ready', detail });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            status: 'error',
            message: err instanceof Error ? err.message : 'データを取得できませんでした',
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (state.status === 'loading') {
    return <div className={`${panel} mt-4 text-sm text-text-muted`}>よみこみ中…</div>;
  }

  if (state.status === 'error') {
    return (
      <div role="alert" className={`${alert} mt-4`}>
        <p className={alertTitle}>大会が見つかりません</p>
        <p className="mt-1 text-sm">{state.message}</p>
      </div>
    );
  }

  const { detail } = state;
  const champion = championOf(detail);

  return (
    <div className="space-y-6 pt-4">
      <header>
        <div className="flex flex-wrap items-baseline gap-3">
          <h1 className={heading}>{detail.name}</h1>
          {/* 開催中は黄（docs/DESIGN.md が `yellow` に「開催中の大会」を割り当てている）。 */}
          {detail.status !== 'FINISHED' && <span className={badgeYellow}>かいさい中</span>}
        </div>
        <p className={`${lead} mt-2 text-sm`}>
          {champion ? `ゆうしょう ${champion.name}` : `${detail.maxEntries} 人のトーナメント`}
        </p>
      </header>

      {/*
        **木を線で結ばない。** 線でつなぐと、狭い画面で必ず横にはみ出す。ラウンドを列にして
        並べるだけで「勝ち上がり」は読めるので、はみ出さないことを優先する。
      */}
      <div className="grid gap-4 sm:grid-cols-3">
        {roundsOf(detail).map((round) => (
          <section key={round.round} className="space-y-3">
            <h2 className={sectionTitle}>{roundName(round.round, detail.totalRounds)}</h2>
            {round.matches.map((match) => (
              <MatchCard key={match.id} match={match} />
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}

function MatchCard({ match }: { match: BracketMatch }) {
  return (
    <article className={panel}>
      <SideRow side={match.a} isSettled={match.isSettled} />
      <div className="my-1 text-center text-[11px] text-text-muted">vs</div>
      <SideRow side={match.b} isSettled={match.isSettled} />
    </article>
  );
}

function SideRow({ side, isSettled }: { side: BracketSide; isSettled: boolean }) {
  if (!side.character) {
    // **枠は戦う前から存在する**（ADR-023）。空欄ではなく「勝ち上がり待ち」と伝える。
    return <p className={`${bracketSlot} text-text-muted`}>まだきまっていません</p>;
  }

  return (
    <p className={`${bracketSlot} flex items-baseline justify-between gap-2`}>
      <span className={side.isWinner ? 'font-display' : undefined}>{side.character.name}</span>
      {/* 勝敗は**色ではなく書体と言葉**で示す（赤は主要 CTA の専有。docs/DESIGN.md §4）。 */}
      {isSettled && (
        <span className="shrink-0 text-[11px] text-text-muted">
          {side.isWinner ? 'かち' : 'まけ'}
        </span>
      )}
    </p>
  );
}
